"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  OrganizationsIcon,
  PlusIcon,
  UsersIcon,
  OnlineIcon,
  ServerIcon,
  ArrowRightIcon,
  GlobeIcon,
  LockIcon,
  SettingsIcon,
} from "@/components/icons";
import {
  getServerLimitForSubscription,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "@/lib/billing/plans";

export default function MyOrganizationsPage() {
  const organizations = useQuery(api.organizations.getOwned);
  const user = useQuery(api.users.getCurrentUser);

  const currentTier = (user?.subscriptionTier ?? "free") as SubscriptionTier;
  const currentStatus = (user?.subscriptionStatus ?? "none") as SubscriptionStatus;
  const serverLimit = getServerLimitForSubscription(currentTier, currentStatus);
  const canCreateMore =
    organizations !== undefined && serverLimit > 0 && organizations.length < serverLimit;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            My Organizations
          </h1>
          <p className="mt-1 text-muted">
            Manage organizations you own and operate
          </p>
        </div>
        <Link href="/dashboard/my-orgs/new">
          <Button variant={canCreateMore ? "default" : "outline"}>
            <PlusIcon className="mr-2 h-4 w-4" />
            {canCreateMore ? "Create Organization" : "Upgrade to Add Server"}
          </Button>
        </Link>
      </div>

      {/* Organizations List */}
      {organizations === undefined ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : organizations.length === 0 ? (
        <Card className="py-16">
          <CardContent className="text-center">
            <OrganizationsIcon className="mx-auto h-12 w-12 text-muted" />
            <h2 className="mt-4 text-xl font-semibold text-foreground">
              No organizations yet
            </h2>
            <p className="mt-2 text-muted">
              Create your first organization to get started
            </p>
            <Link href="/dashboard/my-orgs/new" className="mt-6 inline-block">
              <Button>
                <PlusIcon className="mr-2 h-4 w-4" />
                Create Organization
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <OrgCard key={org._id} org={org} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrgCard({
  org,
}: {
  org: {
    _id: string;
    name: string;
    slug: string;
    description?: string;
    serverIp?: string;
    visibility: "public" | "page_only" | "private";
    memberCount: number;
    onlineCount: number;
  };
}) {
  const VisibilityIcon = org.visibility === "private" ? LockIcon : GlobeIcon;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg">{org.name}</CardTitle>
          <Badge variant="outline" className="gap-1">
            <VisibilityIcon className="h-3 w-3" />
            {org.visibility === "page_only" ? "Unlisted" : org.visibility}
          </Badge>
        </div>
        {org.description && (
          <p className="line-clamp-2 text-sm text-muted">{org.description}</p>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        {/* Stats */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-muted">
            <UsersIcon className="h-4 w-4" />
            <span>{org.memberCount} members</span>
          </div>
          <div className="flex items-center gap-1 text-primary">
            <OnlineIcon className="h-4 w-4" />
            <span>{org.onlineCount} online</span>
          </div>
        </div>

        {/* Server IP */}
        {org.serverIp && (
          <div className="flex items-center gap-2 text-sm text-muted">
            <ServerIcon className="h-4 w-4" />
            <code>{org.serverIp}</code>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Link href={`/org/${org.slug}`} className="flex-1">
            <Button variant="outline" className="w-full">
              View
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link href={`/org/${org.slug}/settings`}>
            <Button variant="ghost" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              Manage
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
